import React, { useEffect, useCallback, useRef, useContext, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import dayjs from 'dayjs';
import {
  LoyaltyRedemptionCard,
  Image,
  Heading,
  ImageSizeEnum,
  HeadingEnum,
  TextColorEnum,
  BoldEnum,
  Text,
  TextEnum,
  TitleAndBack,
  CouponsRedemptionCard,
} from '@desquared/wave-components-library';
import { ResponsiveMenuStore } from '@/store/sideSheet';
import { getUserLoyaltySummary, getUserLoyaltyTransactions } from '@/api/loyalty';
import { CurrencyContext } from '@/contexts/CurrencyProvider';
import { useTranslations } from 'next-intl';
import { getUserV3TransactionsSummary } from '@/api/wave/users';
import LoaderStore from '@/store/loader';

import styles from './LoyaltyTransactionsHistory.module.css';

export default function LoyaltyTransactionsHistory({ user, image }) {
  const t = useTranslations('user_loyalty');
  const router = useRouter();
  const { token } = user;
  const observer = useRef();
  const { formatter, currency } = useContext(CurrencyContext);
  const [userData, setUserData] = useState(null);
  const openMenu = () => {
    ResponsiveMenuStore.open();
  };

  useEffect(() => {
    async function getUserLoyaltyTransactionsSummary() {
      await getUserLoyaltySummary();
    }
    getUserLoyaltyTransactionsSummary();
  }, []);

  const fetchTransactions = async ({ pageParam }) => {
    const results = await getUserLoyaltyTransactions({ pageNumber: pageParam.toString(), pageSize: 15 });
    return results;
  };

  const {
    data: transactionsResponse,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['user/loyalty/history/', router.locale],
    queryFn: fetchTransactions,
    enabled: !!token,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage?.data?.length === 0) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
      if (firstPageParam <= 1) {
        return undefined;
      }
      return firstPageParam - 1;
    },
    initialPageParam: 0,
    staleTime: 0,
    select: data => data.pages.flatMap(page => page.data.map(transformTransactions)),
  });

  const transformTransactions = transaction => {
    return {
      orderId: transaction.receiptNumber,
      orderDate: transaction.transactionDate,
      orderTime: transaction.transactionDate,
      amountSpent: transaction.amountSpent,
      collectedPoints: transaction.pointsCollected,
      redeemPoints: transaction.pointsRedeemed,
      discountAmount: transaction.discountAmount,
      source: transaction.source,
    };
  };

  const lastResultItemRef = useCallback(
    node => {
      if (!node) return;
      if (isLoading || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      });

      observer.current.observe(node);
    },
    [isLoading, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    if (!user) return;
    LoaderStore.addEvent('LOAD_USER_LOYALTY_SUMMARY');

    getUserV3TransactionsSummary(token)
      .then(data => {
        setUserData(data);
      })
      .catch(error => {
        console.error('Error fetching user summary:', error);
      })
      .finally(() => {
        LoaderStore.removeEvent('LOAD_USER_LOYALTY_SUMMARY');
      });
  }, [user]);
  return (
    <div className={styles.loyaltyContainer}>
      <TitleAndBack wording={t('loyalty_transaction_history')} onBack={() => openMenu()} />
      {!isLoading && (transactionsResponse?.length === 0 || !transactionsResponse) && (
        <div className={styles.emptyListContainer}>
          <Image size={ImageSizeEnum.NoAddresses} imgSrc={image} imgTitle={t('no_loyalty_transactions')} />
          <Heading
            classOption={styles.notFoundWording}
            type={HeadingEnum.HeadingS}
            color={TextColorEnum.OnSurfaceHighEmphasis}
            bold={BoldEnum.Bold}
          >
            {t('no_loyalty_transactions')}
          </Heading>
          <Text type={TextEnum.Assistive} color={TextColorEnum.OnSurfaceLowEmphasis}>
            {t('no_loyalty_transactions_subtext')}
          </Text>
        </div>
      )}
      {!isLoading && transactionsResponse && transactionsResponse?.length > 0 && (
        <div className={styles.redemptionSummaryCardContainer}>
          <CouponsRedemptionCard
            pointsValue={userData?.totalLoyaltyPoints}
            postfix={t('redemptionCardSummary.postfix')}
            orders={userData?.totalTransactions}
            redeemedPoints={userData?.totalRedeemedPoints}
            savings={userData?.totalSavings}
            currencyFormatter={formatter}
            currentBalanceLabel={t('currentBalanceLabel')}
            totalOrdersLabel={t('totalOrdersLabel')}
            totalRedeemedPointsLabel={t('totalRedeemedPointsLabel')}
            totalSavingsLabel={t('totalSavingsLabel')}
          />
        </div>
      )}
      {!isLoading &&
        transactionsResponse?.map((transaction, pageIndex) => {
          const isLast = pageIndex === transactionsResponse.length - 1;
          return (
            <div key={transaction.orderId} ref={isLast ? lastResultItemRef : null}>
              <LoyaltyRedemptionCard
                orderId={transaction.orderId}
                orderDate={dayjs(transaction.orderDate).format('MMM D, YYYY')}
                orderTime={dayjs(transaction.orderTime).format('HH:mm')}
                amountWording={t('amount_spent')}
                collectWording={t('amount_collected')}
                redeemWording={t('amount_redeemed')}
                amount={formatter.format(transaction.amountSpent / 100)}
                collectedPoints={transaction.collectedPoints}
                redeemPoints={`${transaction.redeemPoints !== 0 ? '-' : ''}${transaction.redeemPoints}`}
                labelText={`${new Intl.NumberFormat(router.locale, {
                  style: 'currency',
                  currency,
                  currencyDisplay: 'narrowSymbol',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                }).format(transaction.discountAmount / 100)} ${t('discount')}`}
              />
            </div>
          );
        })}
    </div>
  );
}
